import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import StockSearchForm from './components/StockSearchForm'
import { PaginatedStock, Stock } from 'types/stock'
import { Container, Stack, Typography } from '@airbus/components-react/components'
import StockDetail from './components/StockDetail'
import useQuery from 'utils/hooks/useQuery'
import urlApis from 'utils/api/urlApis'
import LangContext from 'context/LangContext'
import StockInterchangeableTable from './components/StockInterchangeableTable'
import PageHeader from 'components/pageHeader'
import { useSearchParams } from 'react-router-dom'
import myLocalStorage from 'utils/myLocalStorage'
import ComponentsUNames from 'constants/componentsUNames'

export type StockSearchInputs = { pn: string; company: string; store: string }

export type Interchangeables = PaginatedStock & {
  filters: StockSearchInputs
}

const StockPage = () => {
  const [stock, setStock] = useState<Stock | null>(null)
  const [interchangeables, setInterchangeables] = useState<Interchangeables | null>(null)

  const { fetchData: fetchInterchangeable } = useQuery<PaginatedStock>(urlApis.URL_STOCK_INTERCHANGEABLE_GET)
  const { fetchData: fetchStock } = useQuery<Stock>(urlApis.URL_STOCK_GET)

  const { t } = useTranslation()
  const { lang } = useContext(LangContext)
  const [searchParams] = useSearchParams()
  const pn = searchParams.get('pn') || ''
  const company =
    searchParams.get('company') ||
    myLocalStorage.getOrDefault([myLocalStorage.KEY_USER_COMPANY], myLocalStorage.VALUE_TYPE.STRING, undefined)
  const store =
    searchParams.get('store') ||
    myLocalStorage.getOrDefault([myLocalStorage.KEY_USER_STORE], myLocalStorage.VALUE_TYPE.STRING, undefined)

  const handleSubmit = async (data: StockSearchInputs) => {
    const stockResponse = await fetchStock({
      lang,
      pn: data?.pn.toUpperCase(),
      companyCode: data?.company,
      storeCode: data?.store,
    })
    const interchangeableResponse = await fetchInterchangeable({
      lang,
      pn: data?.pn,
      companyCode: data?.company,
      storeCode: data?.store,
      page: interchangeables?.number || 0,
      size:
        interchangeables?.size ||
        myLocalStorage.getOrDefault(
          [
            myLocalStorage.KEY_PREFS,
            ComponentsUNames.STOCKS_STORE_NAME,
            ComponentsUNames.STOCKS_INTERCHANGEABLE_UNAME + '|PAGESIZE',
          ],
          myLocalStorage.VALUE_TYPE.STRING,
          undefined,
        ),
    })

    setStock(stockResponse?.data || null)
    if (interchangeableResponse?.data)
      setInterchangeables({
        ...interchangeableResponse.data,
        filters: { pn: data?.pn, company: data?.company, store: data?.store },
      })
  }

  const handlePaginateInterchangeable = async (size: number, page: number) => {
    const interchangeableResponse = await fetchInterchangeable({
      lang,
      pn: interchangeables?.filters.pn || '',
      companyCode: interchangeables?.filters.company || '',
      storeCode: interchangeables?.filters.store || '',
      page: page - 1,
      size,
    })

    myLocalStorage.put(
      [
        myLocalStorage.KEY_PREFS,
        ComponentsUNames.STOCKS_STORE_NAME,
        ComponentsUNames.STOCKS_INTERCHANGEABLE_UNAME + '|PAGESIZE',
      ],
      size,
    )

    if (interchangeables) {
      setInterchangeables({ ...interchangeables, ...interchangeableResponse?.data })
    }
  }

  useEffect(() => {
    if (pn && company && store) handleSubmit({ pn, company, store })
  }, [])

  const baseUrl = process.env.PUBLIC_URL

  const navItems = [
    {
      title: t('9510083'),
      url: baseUrl + '/stocks',
    },
  ]

  return (
    <>
      <PageHeader pageTitle={t('9510083')} navItems={navItems} />
      <Container style={{ flex: 1 }} until='xl'>
        <Stack spacing='5-x'>
          <StockSearchForm onSubmit={handleSubmit} pn={pn} company={company} store={store} />
          {stock ? (
            <Stack>
              <StockDetail stock={stock} />
              <StockInterchangeableTable
                interchangeables={interchangeables}
                onPaginateInterchangeable={handlePaginateInterchangeable}
              />
            </Stack>
          ) : (
            <Typography style={{ textAlign: 'center' }}>{t('9500055')}</Typography>
          )}
        </Stack>
      </Container>
    </>
  )
}

export default StockPage
